import { useState, useRef, useEffect } from 'react';
import { useGmbPhotos, useUploadPhoto, useDeletePhoto, useGmbReviewsOverview, useGmbVideos, useUploadVideo } from '../../hooks/useGmbImplementation';
import { useGmbConnectionStatus } from '../../hooks/useGmbStrategy';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDuration, loadVideoMetadata, extractVideoThumbnail } from './videoUpload';
import type { GMBVideo } from '../../types';

interface PhotoUploadProps {
  onTooltipShow: (title: string, content: string) => void;
}

const MAX_TOTAL_UPLOADS = 20;

const PHOTO_TYPES = [
  { value: 'profile', label: 'Profile', dims: '250 × 250 px', aspect: 'aspect-square', ctx: 'rounded-full', size: 'w-24 h-24' },
  { value: 'cover', label: 'Cover', dims: '1080 × 608 px', aspect: 'aspect-[1080/608]', ctx: 'rounded-lg', size: 'w-full' },
  { value: 'post', label: 'Posts', dims: '1200 × 900 px', aspect: 'aspect-[1200/900]', ctx: 'rounded-lg', size: 'w-full' },
  { value: 'interior', label: 'Interior', dims: '720 × 720 px', aspect: 'aspect-square', ctx: 'rounded-lg', size: 'w-full' },
  { value: 'exterior', label: 'Exterior', dims: '720 × 720 px', aspect: 'aspect-square', ctx: 'rounded-lg', size: 'w-full' },
  { value: 'product', label: 'Product', dims: '720 × 720 px', aspect: 'aspect-square', ctx: 'rounded-lg', size: 'w-full' },
  { value: 'team', label: 'Team', dims: '720 × 720 px', aspect: 'aspect-square', ctx: 'rounded-lg', size: 'w-full' },
];

const photoRequirements = [
  { type: 'Profile', size: '250×250px', formats: 'JPG, PNG', sizeLimit: '10KB–5MB', icon: 'ri-user-line' },
  { type: 'Cover', size: '1080×608px', formats: 'JPG, PNG', sizeLimit: '10KB–5MB', icon: 'ri-layout-top-line' },
  { type: 'Posts', size: '1200×900px', formats: 'JPG, PNG', sizeLimit: '10KB–5MB', icon: 'ri-article-line' },
  { type: 'Business', size: '720×720px', formats: 'JPG, PNG', sizeLimit: '10KB–5MB', icon: 'ri-store-2-line' },
];

type UploadMode = 'photo' | 'video';

export function GallerySkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {[1, 2, 3].map(i => (
        <Skeleton key={i} className="h-56 rounded-lg" />
      ))}
    </div>
  );
}

export function VideoGrid({
  videos,
  onDelete,
  isDeleting,
}: {
  videos: GMBVideo[];
  onDelete: (id: string) => void;
  isDeleting: boolean;
}) {
  if (videos.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <i className="ri-video-line text-4xl text-muted-foreground/40 mb-3 block"></i>
        <p className="text-sm">No videos uploaded yet.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {videos.map((video) => (
        <Card key={video.id} className="overflow-hidden shadow-sm group">
          <div className="w-full h-40 bg-muted relative">
            {video.thumbnail_url ? (
              <img
                src={video.thumbnail_url}
                alt={video.file_name}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <i className="ri-video-line text-muted-foreground/40 text-3xl"></i>
              </div>
            )}
            <a
              href={video.s3_url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors cursor-pointer"
              title="Open video"
            >
              <i className="ri-play-circle-line text-white text-4xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </a>
            <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-[10px] font-medium">
              {formatDuration(video.duration_seconds)}
            </span>
          </div>
          <CardContent className="p-3 flex items-center justify-between">
            <div>
              <p className="text-xs text-foreground truncate max-w-[180px]" title={video.file_name}>
                {video.file_name || 'Video'}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {video.video_resolution || 'MP4'} · {video.file_size_mb != null ? `${video.file_size_mb} MB` : ''}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(video.id)}
              disabled={isDeleting}
              className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function PhotoUpload({ onTooltipShow }: PhotoUploadProps) {
  const [dragOver, setDragOver] = useState(false);
  const [photoType, setPhotoType] = useState('profile');
  const [uploadMode, setUploadMode] = useState<UploadMode>('photo');
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('uploaded');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: photosData, isLoading, error, refetch } = useGmbPhotos();
  const uploadMutation = useUploadPhoto();
  const uploadVideoMutation = useUploadVideo();
  const deleteMutation = useDeletePhoto();
  const { data: videosData } = useGmbVideos();
  const { data: connectionData } = useGmbConnectionStatus();
  const { data: reviewOverviewData } = useGmbReviewsOverview();

  const businessName = connectionData?.business_name || 'Business Name';
  const avgRating = reviewOverviewData?.data?.avg_rating || 0;
  const totalReviews = reviewOverviewData?.data?.total_reviews || 0;
  const isOpen = connectionData?.is_open ?? true;
  const category = connectionData?.category || null;
  const isServiceArea = Boolean(connectionData?.service_area);

  const photos = photosData?.data?.photos || [];
  const uploadedCount = photosData?.data?.uploaded_count || 0;
  const remainingCount = photosData?.data?.remaining_count || 0;
  const videosCount = photosData?.data?.videos_count || 0;
  // Prefer the GBP-synced photo (is_published_to_gmb=true — what's actually
  // live on Google) over a platform upload of the same type, when both
  // exist. Multiple rows can share a photo_type; a platform upload with a
  // more recent uploaded_at would otherwise silently shadow the real synced
  // photo in this preview (UAT #22).
  const photosOfType = photos.filter((p: any) => p.photo_type === photoType);
  const storedPhotoOfType =
    photosOfType.find((p: any) => p.is_published_to_gmb) || photosOfType[0] || null;
  const storedPhoto = storedPhotoOfType || photos[0] || null;
  const previewSrc = previewUrl || storedPhoto?.s3_url || null;
  const selectedType = PHOTO_TYPES.find((t) => t.value === photoType) || PHOTO_TYPES[0];

  const videos: GMBVideo[] = videosData?.data?.videos || [];
  const videosUploaded = videosData?.data?.videos_uploaded || 0;

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setFilePreview(files[0]);
      handleUpload(files[0]);
    }
  };

  const handleClickBrowse = () => fileInputRef.current?.click();

  const setFilePreview = (file: File) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (uploadMode === 'photo' && file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
  };

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFilePreview(file);
      handleUpload(file);
    }
    e.target.value = '';
  };

  const handleUpload = async (file: File) => {
    if (uploadMode === 'photo') {
      if (!file.type.startsWith('image/')) {
        showToast('Only image files (JPG, PNG) are supported');
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('photo_type', photoType);
      formData.append('user_id', localStorage.getItem('user_id') || '');

      try {
        await uploadMutation.mutateAsync(formData);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        showToast('Photo uploaded successfully');
      } catch (err: any) {
        showToast(err.message || 'Upload failed');
      }
      return;
    }

    // Video mode
    if (!file.type || !file.type.includes('mp4')) {
      showToast('Only MP4 videos are supported');
      return;
    }
    if (file.size > 75 * 1024 * 1024) {
      showToast('Video exceeds the 75MB limit');
      return;
    }

    setUploading(true);
    try {
      const { seconds, resolution } = await loadVideoMetadata(file);
      const thumbnail = await extractVideoThumbnail(file);

      const formData = new FormData();
      formData.append('user_id', localStorage.getItem('user_id') || '');
      formData.append('file', file);
      if (thumbnail) formData.append('thumbnail', thumbnail, `${file.name}_thumb.jpg`);
      if (seconds != null) formData.append('duration_seconds', String(Math.max(1, Math.min(120, Math.round(seconds)))));
      if (resolution) formData.append('video_resolution', resolution);

      await uploadVideoMutation.mutateAsync(formData);
      showToast('Video uploaded successfully');
    } catch (err: any) {
      showToast(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId: string) => {
    try {
      await deleteMutation.mutateAsync(photoId);
      showToast('Deleted');
    } catch (err: any) {
      showToast(err.message || 'Delete failed');
    }
  };

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-destructive text-3xl mb-3 block"></i>
        <h3 className="text-sm font-semibold text-destructive">Failed to load photos</h3>
        <p className="text-xs text-destructive/80 mt-1">{error.message}</p>
        <Button onClick={() => refetch()} className="mt-3" variant="destructive" size="sm">
          Retry
        </Button>
      </div>
    );
  }

  const uploadProgressPct = uploadedCount === 0
    ? 0
    : Math.round((uploadedCount / MAX_TOTAL_UPLOADS) * 100);

  const galleryPhotos = photos;
  const galleryVideos = videos;
  const showPhotos = uploadedCount > 0 || photos.length > 0;

  return (
    <div className="space-y-6 relative">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-zinc-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2">
          <i className="ri-check-line text-green-400"></i>
          {toast}
        </div>
      )}

      {/* BRD tabs — filter the bottom gallery */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="uploaded">Uploaded</TabsTrigger>
          <TabsTrigger value="remaining">Remaining</TabsTrigger>
          <TabsTrigger value="videos">Videos</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Two-column layout: upload dropzone (Photo/Video toggle) + GMB preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Upload area */}
        <div className="space-y-4">
          <div className="inline-flex rounded-lg border border-border p-1 bg-muted">
            {(['photo', 'video'] as UploadMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setUploadMode(mode)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  uploadMode === mode
                    ? 'bg-background text-foreground shadow'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {mode === 'photo' ? 'Photo' : 'Video'}
              </button>
            ))}
          </div>

          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={!uploadMutation.isPending && !uploading ? handleClickBrowse : undefined}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              uploading || uploadMutation.isPending
                ? 'border-primary bg-primary/5 cursor-wait'
                : dragOver
                  ? 'border-primary bg-primary/5 cursor-pointer'
                  : 'border-border hover:border-muted-foreground/40 bg-card cursor-pointer'
            }`}
          >
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              {uploading || uploadMutation.isPending ? (
                <i className="ri-loader-4-line animate-spin text-xl text-primary"></i>
              ) : (
                <i className={`${uploadMode === 'photo' ? 'ri-upload-cloud-line' : 'ri-video-upload-line'} text-xl text-muted-foreground`}></i>
              )}
            </div>
            <p className="text-sm font-medium">
              {uploading || uploadMutation.isPending
                ? 'Uploading...'
                : `Drag ${uploadMode === 'photo' ? 'photos' : 'videos'} here or click to browse`}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {uploadMode === 'photo'
                ? 'JPG, PNG — 10KB to 5MB'
                : 'MP4 — 1 to 120 seconds, up to 75MB'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept={uploadMode === 'photo' ? 'image/*' : 'video/mp4,.mp4'}
              multiple={uploadMode === 'photo'}
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {uploadMode === 'photo' && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Photo Type</label>
              <select
                value={photoType}
                onChange={(e) => setPhotoType(e.target.value)}
                className="w-full border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground"
              >
                {PHOTO_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Right: GMB Listing Preview — type-aware, live */}
        <div className="bg-card rounded-lg border border-border p-5 shadow-sm">
          <h4 className="text-xs font-semibold text-foreground mb-3">GMB Listing Preview</h4>
          <div className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-2 py-0.5 rounded">
                {selectedType.label} photo
              </span>
              <span className="text-[10px] text-muted-foreground">{selectedType.dims}</span>
            </div>

            <div className={`${selectedType.aspect} ${selectedType.size} ${selectedType.ctx} bg-muted overflow-hidden flex items-center justify-center`}>
              {previewSrc ? (
                <img
                  src={previewSrc}
                  alt="Photo preview"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground/50">
                  <i className="ri-image-line text-3xl"></i>
                  <span className="text-[10px] mt-1">No photo yet</span>
                </div>
              )}
            </div>

            <div className="flex items-start gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{businessName}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <i className="ri-star-fill text-yellow-400 text-xs"></i>
                  <span className="text-xs text-muted-foreground">{avgRating > 0 ? `${avgRating} (${totalReviews} reviews)` : 'No reviews yet'}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              {isOpen && (
                <span className="px-2 py-1 rounded-md bg-primary/10 text-primary text-[10px] font-medium">Open now</span>
              )}
              {isServiceArea && (
                <span className="px-2 py-1 rounded-md bg-muted text-muted-foreground text-[10px]">Service area business</span>
              )}
              {category && (
                <span className="px-2 py-1 rounded-md bg-muted text-muted-foreground text-[10px]">{category}</span>
              )}
            </div>

            <p className="text-[10px] text-muted-foreground border-t border-border pt-2">
              {previewUrl
                ? 'Live preview of the photo you selected — this is how it will look on your listing.'
                : storedPhotoOfType
                  ? `Showing your stored ${selectedType.label.toLowerCase()} photo. Pick a new file to preview it live.`
                  : `Pick a ${selectedType.label.toLowerCase()} photo above to see it here in context.`}
            </p>
          </div>
        </div>
      </div>

      {/* Photo Requirements */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Photo Requirements</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onTooltipShow('Photo Requirements', 'Each photo type has specific size, format, and file size requirements for Google Business Profile. Upload photos that meet these specs for best results.')}
            className="text-muted-foreground hover:text-foreground"
            title="More info"
          >
            <i className="ri-information-line text-lg"></i>
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {photoRequirements.map((req) => (
            <Card key={req.type} className="p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center text-primary">
                  <i className={`${req.icon} text-xs`}></i>
                </div>
                <span className="text-sm font-medium text-foreground">{req.type}</span>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Size:</span> {req.size}
                </div>
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Format:</span> {req.formats}
                </div>
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Limit:</span> {req.sizeLimit}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Photo Gallery — unified, at the bottom (per BRD), filtered by tabs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Photo Gallery</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onTooltipShow('Photo Gallery', 'Photos and videos you uploaded to the platform. These can later be used to create GMB posts.')}
            className="text-muted-foreground hover:text-foreground"
            title="More info"
          >
            <i className="ri-information-line text-lg"></i>
          </Button>
        </div>

        {activeTab === 'remaining' ? (
          <div className="space-y-4">
            <Card className="p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-muted rounded-md flex items-center justify-center text-muted-foreground">
                  <i className="ri-image-add-line text-sm"></i>
                </div>
                <span className="text-sm font-medium text-muted-foreground">Remaining</span>
              </div>
              <div className="text-2xl font-bold">{remainingCount}</div>
              <p className="text-xs text-muted-foreground mt-1">
                of {MAX_TOTAL_UPLOADS} upload slots remaining
              </p>
              <div className="mt-4">
                <Progress value={uploadProgressPct} className="h-2" />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">{uploadedCount} photos uploaded</span>
                  <span className="text-xs text-muted-foreground">{videosUploaded} videos uploaded</span>
                </div>
              </div>
            </Card>

            <div className="text-center py-8 text-muted-foreground border border-dashed border-border rounded-lg">
              <i className="ri-upload-cloud-line text-4xl text-muted-foreground/40 mb-3 block"></i>
              <p className="text-sm">Upload photos to fill your remaining {remainingCount} slots.</p>
            </div>
          </div>
        ) : (
          <>
            {isLoading ? (
              <GallerySkeleton />
            ) : activeTab === 'videos' ? (
              <VideoGrid videos={galleryVideos} onDelete={handleDelete} isDeleting={deleteMutation.isPending} />
            ) : !showPhotos ? (
              <div className="text-center py-8 text-muted-foreground">
                <i className="ri-image-line text-4xl text-muted-foreground/40 mb-3 block"></i>
                <p className="text-sm">No photos uploaded yet. Use the upload area above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {galleryPhotos.map((photo: any) => (
                  <Card key={photo.id} className="overflow-hidden shadow-sm group">
                    <div className="w-full h-40 bg-muted">
                      {photo.s3_url ? (
                        <img
                          src={photo.s3_url}
                          alt={photo.file_name}
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-muted-foreground/40 text-3xl"></i>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-3 flex items-center justify-between">
                      <div>
                        <Badge variant="secondary" className="text-[10px] font-medium">
                          {photo.photo_type}
                        </Badge>
                        <p className="text-xs text-foreground mt-1 truncate max-w-[180px]">{photo.file_name}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(photo.id)}
                        disabled={deleteMutation.isPending}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
